import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, setUnauthorizedHandler, Tokens, tokenStore } from '../api/client';
import { PageLoader } from '../components';

export type UserRole =
  | 'SuperAdmin' | 'RegionalManager' | 'OperationsSupervisor' | 'Finance' | 'TeamLead' | 'Washer'
  | 'PartnerAdmin' | 'PartnerViewer';

export interface User {
  id: string; tenantId: string; partnerId?: string | null; countryId?: string | null;
  fullName: string; email?: string | null; phone?: string | null; role: UserRole;
  preferredLanguage: string; hasPin: boolean; isActive: boolean;
}

interface AuthResponse extends Tokens { user: User }

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<User>;
  pinLogin: (userId: string, pin: string) => Promise<User>;
  setPin: (pin: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const DEVICE_KEY = 'gr.deviceKey';
const LAST_USER_KEY = 'gr.lastUser';

/** Stable per-browser id used for "remember this device" + PIN login. */
export function getDeviceKey(): string {
  try {
    let key = localStorage.getItem(DEVICE_KEY);
    if (!key) {
      key = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
      localStorage.setItem(DEVICE_KEY, key);
    }
    return key;
  } catch {
    return 'ephemeral';
  }
}

export interface LastUser { id: string; fullName: string; hasPin: boolean }
export function getLastUser(): LastUser | null {
  try { const raw = localStorage.getItem(LAST_USER_KEY); return raw ? (JSON.parse(raw) as LastUser) : null; } catch { return null; }
}

function platform(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'web';
}

export function AuthProvider({ children, allowedRoles }: { children: ReactNode; allowedRoles?: UserRole[] }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const remember = (u: User) => {
    try { localStorage.setItem(LAST_USER_KEY, JSON.stringify({ id: u.id, fullName: u.fullName, hasPin: u.hasPin } satisfies LastUser)); } catch { /* ignore */ }
  };

  const applyAuth = useCallback((res: AuthResponse): User => {
    if (allowedRoles && !allowedRoles.includes(res.user.role)) {
      throw Object.assign(new Error('This account cannot use this app.'), { code: 'wrong_app' });
    }
    tokenStore.set({ accessToken: res.accessToken, accessTokenExpiresAtUtc: res.accessTokenExpiresAtUtc, refreshToken: res.refreshToken });
    setUser(res.user);
    remember(res.user);
    return res.user;
  }, [allowedRoles]);

  const refreshUser = useCallback(async () => {
    if (!tokenStore.get()) { setUser(null); return; }
    try {
      const me = await api<User>('/api/auth/me');
      setUser(me);
      remember(me);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => { tokenStore.set(null); setUser(null); });
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    login: async (identifier, password) =>
      applyAuth(await api<AuthResponse>('/api/auth/login', { method: 'POST', auth: false,
        body: { identifier, password, deviceKey: getDeviceKey(), deviceName: navigator.userAgent.slice(0, 80), platform: platform() } })),
    pinLogin: async (userId, pin) =>
      applyAuth(await api<AuthResponse>('/api/auth/pin-login', { method: 'POST', auth: false, body: { userId, pin, deviceKey: getDeviceKey() } })),
    setPin: async (pin) => {
      await api('/api/auth/pin', { method: 'POST', body: { pin } });
      setUser((u) => { const next = u ? { ...u, hasPin: true } : u; if (next) remember(next); return next; });
    },
    logout: async () => {
      const t = tokenStore.get();
      try { await api('/api/auth/logout', { method: 'POST', body: { refreshToken: t?.refreshToken } }); } catch { /* best effort */ }
      tokenStore.set(null);
      setUser(null);
    },
    refreshUser,
  }), [user, loading, applyAuth, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Wrap routes that need a signed-in user; optionally restrict by role. */
export function RequireAuth({ children, roles }: { children: ReactNode; roles?: UserRole[] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
